import { useEffect, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { Ellipsis, Search, Settings, Tv, MonitorSmartphone, UserRound } from 'lucide-react';
import { BrandMark } from '@/lib/brand';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { readDisplayPreferences, subscribeDisplayPreferences } from '@/lib/display-preferences';
import { ProfileMenu } from './profile-menu';

// Same threshold as the @display-media(max-width:767px) rules, in logical CSS px.
const compactLayout = () => (readDisplayPreferences().supported ? readDisplayPreferences().width : innerWidth) < 768;

export type NavItem = { id: string; name: string; icon: ComponentType<{ size?: number }> };

// Top bar on wide layouts; five-tab bar plus a More sheet on phones. Every
// destination in `items` stays reachable in both layouts; nothing is dropped.
// The overflow menu (Base UI Menu) and More sheet (Base UI Dialog) provide focus
// management, keyboard navigation, inert background, scroll lock and focus return.
const PRIMARY = ['home', 'discover', 'library', 'calendar', 'addons'];
const TABS = ['home', 'discover', 'library', 'addons'];

export function AppNav({ items, route, go, native, account, onAccount, signIn }: {
  items: NavItem[]; route: string; go: (id: string) => void; native: boolean;
  account: ReactNode; onAccount: () => void; signIn?: ReactNode;
}) {
  // Any route change (including hash changes from elsewhere) closes both layers,
  // and they stay closed on return: the reset happens during render, not in an
  // effect, so a stale open state can never be shown for the new route.
  const [menu, setMenuState] = useState(false), [sheet, setSheetState] = useState(false), [openedRoute, setOpenedRoute] = useState(route);
  if (openedRoute !== route) { setOpenedRoute(route); if (menu) setMenuState(false); if (sheet) setSheetState(false); }
  const setMenu = (open: boolean) => setMenuState(open), setSheet = (open: boolean) => setSheetState(open);
  const secondary = items.filter(n => !PRIMARY.includes(n.id) && !['search', 'settings'].includes(n.id));
  const more = items.filter(n => !TABS.includes(n.id) && !['search', 'settings'].includes(n.id));
  const screen = native ? 'receiver' : 'devices';
  // Close both layers when navigation happens elsewhere (hash change, back button,
  // links) or the layout may switch between top bar and tab bar.
  const compact = useRef(compactLayout());
  useEffect(() => {
    // Only a switch between top bar and tab bar closes the layers, so ordinary
    // resizes (mobile browser chrome, on-screen keyboard) don't dismiss them.
    const check = () => { const next = compactLayout(); if (next !== compact.current) { compact.current = next; setMenuState(false); setSheetState(false); } };
    addEventListener('resize', check);
    const unsubscribe = subscribeDisplayPreferences(check);
    return () => { removeEventListener('resize', check); unsubscribe(); };
  }, []);
  const current = (id: string) => (route === id ? 'page' as const : undefined);
  const choose = (id: string) => { setMenu(false); setSheet(false); go(id); };
  const account_ = () => { setMenu(false); setSheet(false); onAccount(); };
  return <>
    <nav className="app-nav" aria-label="Streamit navigation">
      <button className="brand" onClick={() => choose('home')} aria-label="Streamit home">
        <BrandMark className="brand-mark" />streamit<span className="brand-dot">.</span>
      </button>
      <div className="nav-links">
        {items.filter(n => PRIMARY.includes(n.id)).map(n => <button key={n.id} className="nav-link" aria-current={current(n.id)} onClick={() => choose(n.id)}>{n.name}</button>)}
      </div>
      <div className="nav-actions">
        <button className="nav-search" aria-label="Search" aria-current={current('search')} onClick={() => choose('search')}><Search size={18} /><span>Search titles</span></button>
        <button className="nav-pill nav-screen" aria-label={native ? 'This computer' : 'Watch on TV'} onClick={() => choose(screen)}>{native ? <MonitorSmartphone size={18} /> : <Tv size={18} />}<span>{native ? 'This computer' : 'Watch on TV'}</span></button>
        <button className="nav-pill nav-settings" aria-label="Settings" title="Settings" aria-current={current('settings')} onClick={() => choose('settings')}><Settings size={20}/></button>
        {!!secondary.length && <div className="nav-menu">
          <DropdownMenu open={menu} onOpenChange={setMenu}>
            <DropdownMenuTrigger className="nav-pill" aria-label="More destinations"><Ellipsis size={18} /></DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={8} className="nav-menu-list">
              {secondary.map(n => <DropdownMenuItem key={n.id} aria-current={current(n.id)} onClick={() => choose(n.id)}><n.icon size={18} />{n.name}</DropdownMenuItem>)}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={account_}><UserRound size={18} /><span>Your account<small>Library and sign-in</small></span></DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>}
        {signIn}
        <ProfileMenu account={account} route={route} />
      </div>
    </nav>
    <nav className="tabbar" aria-label="Streamit tabs">
      {items.filter(n => TABS.includes(n.id)).map(n => <button key={n.id} aria-current={current(n.id)} onClick={() => choose(n.id)}><n.icon size={22} />{n.name}</button>)}
      <Sheet open={sheet} onOpenChange={setSheet}>
        <SheetTrigger aria-current={more.some(n => n.id === route) ? 'page' : undefined} data-more-trigger><Ellipsis size={22} />More</SheetTrigger>
        <SheetContent side="bottom" className="more-sheet">
          <SheetTitle className="more-title">More</SheetTitle>
          {more.map(n => <button key={n.id} aria-current={current(n.id)} onClick={() => choose(n.id)}><n.icon size={20} />{n.name}</button>)}
          <button onClick={() => choose(screen)}>{native ? <MonitorSmartphone size={20} /> : <Tv size={20} />}{native ? 'This computer' : 'Watch on TV'}</button>
          <button onClick={account_}><UserRound size={20} /><span>Your account<small>Library and sign-in</small></span></button>
        </SheetContent>
      </Sheet>
    </nav>
  </>;
}
